export type FieldType =
  | "text"
  | "textarea"
  | "text-rich"
  | "number"
  | "color"
  | "color-alpha"
  | "image"
  | "select"
  | "boolean"
  | "slider"
  | "knob"
  | "layer";

/**
 * Value shape for "text-rich" fields. The text + font + size + format flags
 * travel together so the UI can present a typography control bundle and the
 * template can read them as `props.<key>.text|font|fontSize|bold|italic|underline`
 * in the TSX.
 *
 * The `bold` / `italic` / `underline` flags are FLAT (whole-text only) — they
 * apply to the entire string, not to per-character ranges. Range-based rich
 * text is a future iteration. Flags are optional so existing template values
 * (which never had them) deserialize cleanly with no migration; an undefined
 * flag is treated as false everywhere.
 */
export interface RichTextValue {
  text: string;
  font: string;
  fontSize: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

/** Declares that a field's value drives the animation's total duration.
 *  Total frames = Σ over all timing fields of
 *    (value × unit-in-seconds × scale + offsetSeconds) × fps
 *  (unit "frames" contributes value × scale frames directly).
 *  Multiple annotated fields sum, so minutes+seconds pairs work. */
export interface FieldTiming {
  /** How to interpret the field's numeric value. */
  unit: "seconds" | "minutes" | "frames";
  /** Multiplier, e.g. seconds-per-item when the field is an item count. Default 1. */
  scale?: number;
  /** Constant padding in seconds this field adds (e.g. an outro hold after
   *  a countdown reaches zero). Default 0. */
  offsetSeconds?: number;
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  default: string | number | boolean | RichTextValue;
  required: boolean;
  /** Present when this field determines the animation's duration. */
  timing?: FieldTiming;
  /** Dropdown options for "select" type. */
  options?: string[];
  /** Min value for slider/knob/number. Also min font size for text-rich. */
  min?: number;
  /** Max value for slider/knob/number. Also max font size for text-rich. */
  max?: number;
  /** Step increment for slider/knob/number. */
  step?: number;
  /** text-rich only: value may contain line breaks. The editor renders a
   *  resizable textarea instead of a single-line input, and templates are
   *  expected to honor \n (pre-line in Remotion, tspan expansion in SVG). */
  multiline?: boolean;
}

/** All available field types with display labels. */
// Plain "text" / "textarea" are retired: every text field is text-rich so
// users always get typography controls and schema<->TSX string drift can't
// happen. The FieldType union keeps the legacy values only so pre-migration
// schemas still PARSE; they are not offered and the backend rejects them.
export const FIELD_TYPES: { value: FieldType; label: string; description: string }[] = [
  { value: "text-rich", label: "Text + Typography", description: "Text with font and size controls" },
  { value: "number", label: "Number", description: "Number input" },
  { value: "slider", label: "Slider", description: "Range slider with min/max" },
  { value: "knob", label: "Knob", description: "Rotary control with fixed range" },
  { value: "color", label: "Color", description: "Color picker (hex)" },
  { value: "color-alpha", label: "Color + Alpha", description: "Color picker with opacity" },
  { value: "boolean", label: "Toggle", description: "On/off switch" },
  { value: "layer", label: "Layer", description: "Toggleable layer visibility (eye switch)" },
  { value: "select", label: "Dropdown", description: "Select from options" },
  { value: "image", label: "Image URL", description: "Image URL input" },
];

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  tsx_code: string;
  field_schema: FieldDefinition[];
  /** Saved defaults for the field values (defaults.json). For SVG templates
   *  these fill the `{{key}}` tokens for card/preview rendering. */
  default_props: Record<string, unknown>;
  thumbnail_url: string | null;
  /** What the template produces. Missing = "remotion" (rows predating SVG). */
  project_kind: "remotion" | "svg";
  version: number;
  is_public: boolean;
  user_id: string;
  /** ID of the prompt that produced the saved TSX — typically the LATEST
   * iteration in the chain. Walk parent_id up via /prompts/:id/root to
   * recover the user's original ask. */
  prompt_id: string | null;
  duration_frames: number;
  fps: number;
  width: number;
  height: number;
  created_at: string;
  updated_at: string;
}

export interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  tsx_code: string;
  field_schema: FieldDefinition[];
  changelog: string;
  created_at: string;
}

export interface CreateTemplateRequest {
  name: string;
  description: string;
  category: string;
  tsx_code: string;
  field_schema: FieldDefinition[];
  default_props: Record<string, unknown>;
  prompt_id?: string;
  tags?: string[];
  duration_frames?: number;
  fps?: number;
  width?: number;
  height?: number;
  /** Tags the created root's kind so the backend validates SVG sources as
   *  SVG (and skips the font / no-plain-text-field checks). */
  project_kind?: "remotion" | "svg";
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category?: string;
  tsx_code?: string;
  field_schema?: FieldDefinition[];
  is_public?: boolean;
  duration_frames?: number;
  fps?: number;
  width?: number;
  height?: number;
}

/** Mirrors the backend's ListParams — it accepts limit/offset (default
 *  limit 20) plus category slug, search, and filter. Results are always
 *  newest-first. */
export interface TemplateListParams {
  limit?: number;
  offset?: number;
  category?: string;
  search?: string;
  filter?: "mine" | "shared" | "public" | "all";
}

export interface TemplateShare {
  template_id: string;
  user_id: string;
  username: string;
  created_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}
